import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ApiButton, Box, Button, Divider, Grid, KeyValue, Link, SectionHeader } from 'web-library';
import { GenericErrorResponse } from '../api';
import { UseCustomMutationReturn } from '../api-refactored/utils/useCustomMutation';
import {
  CoordinatorProfileRequest,
  CoordinatorProfileResponse,
  IExamContentResponse,
} from '../api/user/model';
import { ContactNumberFormItem } from './form-items/ContactNumberFormItem';
import ControlledAutocomplete from './form-items/ControlledAutocomplete';
import FormHeader from './form-items/FormHeader';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  container: {},
  input: {
    marginBottom: theme.spacing(4),
  },
  keyValue: {
    wordWrap: 'break-word',
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
  },
  link: {
     ...typeTheme(theme, 'buttonSmall'),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  dividerContainer: {
    paddingTop: theme.spacing(2),
  },
}));

const DROPDOWN_PLACEHOLDER = 'Select..';

const GridKeyValue = ({
  className,
  label,
  value,
}: {
  className: string;
  label: string;
  value: string | null;
}) => (
  <Grid xl={5} lg={6} md={12} sm={12} xs={12}>
    <KeyValue keyText={label} valueText={value || '-'} />
  </Grid>
);

export interface Props {
  data: CoordinatorProfileResponse;
  isEditable: boolean;
  onEditClick: () => void;
  convertedData: string | null;
  form: any;
  dataIEXAM: IExamContentResponse;
  isLoadingIEXAM: boolean;
  errorMasterCode: any;
  refetchMasterCode: any;
  updateCoordinatorProfile: UseCustomMutationReturn<
    any,
    AxiosError<GenericErrorResponse>,
    CoordinatorProfileRequest,
    unknown
  >;
}

const CoordinatorProfileDetails = ({
  data,
  isEditable,
  onEditClick,
  convertedData,
  form,
  dataIEXAM,
  isLoadingIEXAM,
  errorMasterCode,
  refetchMasterCode,
  updateCoordinatorProfile,
}: Props) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const nonEditableFields = () => (
    <Grid container>
      <GridKeyValue
        className={classes.keyValue}
        label={t('components->>CompleteProfileDetails.Coordinator Name')}
        value={data?.coordinatorName || null}
      />
      <GridKeyValue
        className={classes.keyValue}
        label={t('components->>CompleteProfileDetails.Date Of Birth')}
        value={convertedData}
      />
      <GridKeyValue
        className={classes.keyValue}
        label={t('components->>CompleteProfileDetails.Private Centre/School')}
        value={data?.organisation.description || null}
      />
    </Grid>
  );

  return (
    <Box className={classes.container}>
      {isEditable ? (
        <>
          <Grid container>
            <SectionHeader title={t('profile->>CreateCandidateForm.Coordinator Particulars')} />

            <Grid container>
              <Grid xs={12}>
                <FormHeader title="Personal Details" />
              </Grid>

              <Grid xs={12}>
                <ControlledAutocomplete
                  id="inputSelect-salutation"
                  name="salutation"
                  control={form.control}
                  label={t('components->>CompleteProfileDetails.Salutation')}
                  options={dataIEXAM?.salutation || []}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                  className={classes.input}
                  defaultValue={data?.salutation}
                  placeholder={DROPDOWN_PLACEHOLDER}
                  error={!!form.formState.errors?.salutation}
                  helperText={form.formState.errors?.salutation?.message?.toString()}
                  loading={isLoadingIEXAM}
                  loadingError={!!errorMasterCode}
                  loadingErrorOnReFetch={refetchMasterCode}
                />
              </Grid>
            </Grid>

            {nonEditableFields()}

            <Grid container>
              <Grid xs={12}>
                <ControlledAutocomplete
                  id="inputSelect-designation"
                  name="designation"
                  control={form.control}
                  label={t('admin->>CompleteProfileDetails.Designation')}
                  options={dataIEXAM?.designation || []}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                  className={classes.input}
                  defaultValue={data?.designation}
                  placeholder={DROPDOWN_PLACEHOLDER}
                  error={!!form.formState.errors?.designation}
                  helperText={form.formState.errors?.designation?.message?.toString()}
                  loading={isLoadingIEXAM}
                  loadingError={!!errorMasterCode}
                  loadingErrorOnReFetch={refetchMasterCode}
                />
              </Grid>
            </Grid>
          </Grid>
          <Box className={classes.dividerContainer}>
            <Divider />
          </Box>
          <Grid container>
            <Grid xs={12}>
              <FormHeader title="Contact Details" />
            </Grid>

            <GridKeyValue
              className={classes.keyValue}
              label={t('components->>CompleteProfileDetails.Email')}
              value={data?.email || null}
            />

            <Grid xs={12}>
              <ContactNumberFormItem
                id="coordinatorProfile"
                containerClassName={classes.formElementContainer}
                formHook={form}
                label={t('components->>CompleteProfileDetails.Contact No.')}
                countryCode={{
                  name: 'countryCode',
                  error: !!form.formState.errors.countryCode,
                  helperText: form.formState.errors.countryCode?.message?.toString(),
                  disabled: !isEditable,
                }}
                number={{
                  name: 'number',
                  error: !!form.formState.errors.number,
                  helperText: form.formState.errors?.number?.message?.toString(),
                  disabled: !isEditable,
                }}
                countryCodeDefaultValue={data?.contactNumber?.countryCode}
                numberDefaultValue={data?.contactNumber?.number}
                isSkipTransform
              />
            </Grid>

            <Button
              id="button-coordinatorProfile-cancel"
              className={classes.backButton}
              variant="outlined"
              onClick={onEditClick}
            >
              {t('components->>CompleteProfileDetails.Cancel')}
            </Button>

            <ApiButton
              id="button-coordinatorProfile-updateParticulars"
              status={updateCoordinatorProfile.status}
              type="submit"
            >
              {t('components->>CompleteProfileDetails.Update Particulars')}
            </ApiButton>
          </Grid>
        </>
      ) : (
        <>
          <Grid container xs={12}>
            <Grid xs={12}>
              <SectionHeader
                title={t('profile->>CreateCandidateForm.Coordinator Particulars')}
              >
                {
                    !isEditable && (
                        <Button id="link-editParticulars" className={classes.link} onClick={onEditClick} variant='text'>
                          Edit Particulars
                        </Button>
                    )
                }
              </SectionHeader>
            </Grid>

            <Grid xs={12}>
              <FormHeader title="Personal Details" />
            </Grid>

            <GridKeyValue
              className={classes.keyValue}
              label={t('components->>CompleteProfileDetails.Salutation')}
              value={data?.salutation.description || null}
            />

            {nonEditableFields()}

            <Grid container>
              <GridKeyValue
                className={classes.keyValue}
                label={t('components->>CompleteProfileDetails.Designation')}
                value={data?.designation.description || null}
              />
            </Grid>
          </Grid>

          <Box className={classes.dividerContainer}>
            <Divider />
          </Box>

          <Grid container>
            <Grid xs={12}>
              <FormHeader title="Contact Details" />
            </Grid>

            <Grid container>
              <GridKeyValue
                className={classes.keyValue}
                label={t('components->>CompleteProfileDetails.Email')}
                value={data?.email || null}
              />

              <Grid container>
                <GridKeyValue
                  className={classes.keyValue}
                  label={t('components->>CompleteProfileDetails.Country Code')}
                  value={data?.contactNumber.countryCode.codeValueDescription || null}
                />
                <GridKeyValue
                  className={classes.keyValue}
                  label={t('components->>CompleteProfileDetails.Contact No.')}
                  value={data?.contactNumber.number || null}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default CoordinatorProfileDetails;
